import { useCallback, useEffect, useRef, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Card, OtSelect, savedOt, Screen, useOts } from "./common";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Santiago" });

const PhotoButton = ({ resourceType, resourceId, testid, label = "Foto" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("resource_type", resourceType); fd.append("resource_id", resourceId);
    setBusy(true);
    try { await api.post("/files", fd); toast.success("Foto subida"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (<>
    <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={up} data-testid={`${testid}-input`} />
    <Button type="button" data-testid={testid} variant="outline" className="rounded-full" disabled={busy} onClick={() => ref.current?.click()}><Camera size={16} className="mr-1" />{busy ? "Subiendo…" : label}</Button>
  </>);
};

const TaskRow = ({ t, canPhoto, onSaved }) => {
  const [pct, setPct] = useState(t.progress || 0);
  const save = async () => {
    try { await api.post(`/operaciones/tasks/${t.id}/progress`, { pct, date: today(), comment: "Registrado desde SYC Terreno" }); toast.success(`${t.name}: ${pct}%`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); setPct(t.progress || 0); }
  };
  return (
    <Card className="space-y-3" >
      <div className="flex justify-between gap-2" data-testid={`terreno-task-${t.id}`}><div className="font-semibold text-sm">{t.name}</div>
        <div className="text-xs text-[#5b6656] whitespace-nowrap">Plan hoy {Math.round(t.planned_today ?? 0)}%</div></div>
      <div className="flex items-center gap-3">
        <Slider data-testid={`terreno-task-slider-${t.id}`} value={[pct]} min={0} max={100} step={5} onValueChange={([v]) => setPct(v)} className="flex-1" />
        <div className="w-12 text-right text-lg font-extrabold">{pct}%</div>
      </div>
      <div className="flex gap-2">
        <Button data-testid={`terreno-task-save-${t.id}`} className="flex-1 rounded-full" disabled={pct === (t.progress || 0)} onClick={save}>Guardar avance</Button>
        {canPhoto && <PhotoButton resourceType="ops_task" resourceId={t.id} testid={`terreno-task-photo-${t.id}`} />}
      </div>
    </Card>
  );
};

const Logbook = ({ ot, log, onSaved }) => {
  const [f, setF] = useState({ activities: "", incidents: "" });
  useEffect(() => { setF({ activities: log?.activities || "", incidents: log?.incidents || "" }); }, [log]);
  const save = async () => {
    try { await api.post(`/operaciones/ots/${ot}/logbook`, { date: today(), weather: log?.weather || "", visits: log?.visits || "", notes: log?.notes || "", ...f }); toast.success("Bitácora guardada"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const closed = log?.status === "cerrado";
  return (
    <Card className="space-y-3" >
      <div className="font-extrabold" data-testid="terreno-logbook">Bitácora de hoy {closed && <span className="text-xs text-[#b42318]">(cerrada)</span>}</div>
      <Textarea data-testid="terreno-log-activities" disabled={closed} rows={3} placeholder="Trabajos realizados hoy" value={f.activities} onChange={(e) => setF({ ...f, activities: e.target.value })} />
      <Textarea data-testid="terreno-log-incidents" disabled={closed} rows={2} placeholder="Incidentes (el cliente los verá en su portal)" value={f.incidents} onChange={(e) => setF({ ...f, incidents: e.target.value })} />
      {!closed && <div className="flex gap-2"><Button data-testid="terreno-log-save" className="flex-1 rounded-full" onClick={save}>Guardar bitácora</Button>
        {log?.id ? <PhotoButton resourceType="logbook" resourceId={log.id} testid="terreno-log-photo" /> : <span className="text-[11px] text-[#5b6656] self-center">Guarde para agregar fotos</span>}</div>}
    </Card>
  );
};

export default function Avance() {
  const { can } = useAuth();
  const ots = useOts("/operaciones/board", (d) => d.map((o) => ({ id: o.id, name: `${o.code} · ${o.title}` })));
  const [ot, setOt] = useState(savedOt());
  const [data, setData] = useState(null);
  const load = useCallback(() => ot && api.get(`/operaciones/ots/${ot}`).then((r) => setData(r.data)).catch(() => setData(null)), [ot]);
  useEffect(() => { load(); }, [load]);
  const parents = new Set((data?.tasks || []).map((t) => t.parent_id).filter(Boolean));
  const leaves = (data?.tasks || []).filter((t) => !parents.has(t.id));
  const log = data?.logbook?.find((l) => l.date === today());
  return (
    <Screen title="Avance de OT" testid="terreno-progress">
      <OtSelect testid="terreno-prog-ot" ots={ots} value={ot} onChange={setOt} />
      {data && <>
        <Card className="flex justify-between text-sm" ><span>Avance real</span><b data-testid="terreno-prog-real">{Math.round(data.summary?.real ?? 0)}%</b></Card>
        {can("operaciones.bitacora.registrar") && <Logbook ot={ot} log={log} onSaved={load} />}
        {can("operaciones.avance.registrar") && (leaves.length === 0 ? <Card className="text-sm text-[#5b6656]">La OT no tiene actividades en el Gantt.</Card>
          : leaves.map((t) => <TaskRow key={t.id} t={t} canPhoto={can("operaciones.evidencias.subir")} onSaved={load} />))}
      </>}
    </Screen>
  );
}
